/**
 * Auth API Contract
 *
 * Defines oRPC contracts for authentication endpoints.
 * Implementation: backend/src/orpc/auth.handlers.ts
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { RegisterInputSchema, LoginInputSchema, RefreshTokenInputSchema, LogoutInputSchema, AuthResponseSchema, CheckStatusResponseSchema, LogoutResponseSchema, RefreshResponseSchema, RequestPasswordResetInputSchema, RequestPasswordResetResponseSchema, VerifyResetOtpInputSchema, ResetPasswordInputSchema, VerifyResetOtpResponseSchema, AuthMessageResponseSchema, } from "@hourly/api/auth";
// ============================================
// Auth Contracts
// ============================================
/**
 * POST /orpc/auth/register
 * Register a new user
 */
export const registerAuth = oc
    .route({
    method: "POST",
    path: "/auth/register",
    summary: "Register a new user",
    tags: ["Auth"],
    successStatus: 201,
})
    .input(RegisterInputSchema)
    .output(AuthResponseSchema);
/**
 * POST /orpc/auth/login
 * Login with email and password
 */
export const loginAuth = oc
    .route({
    method: "POST",
    path: "/auth/login",
    summary: "Login with email and password",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(LoginInputSchema)
    .output(AuthResponseSchema);
/**
 * POST /orpc/auth/logout
 * Logout and invalidate refresh token
 */
export const logoutAuth = oc
    .route({
    method: "POST",
    path: "/auth/logout",
    summary: "Logout and invalidate refresh token",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(LogoutInputSchema)
    .output(LogoutResponseSchema);
/**
 * POST /orpc/auth/refresh
 * Refresh access token using refresh token
 */
export const refreshAuth = oc
    .route({
    method: "POST",
    path: "/auth/refresh",
    summary: "Refresh access token using refresh token",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(RefreshTokenInputSchema)
    .output(RefreshResponseSchema);
/**
 * GET /orpc/auth/check-status
 * Check user's onboarding status
 * Protected route - requires JWT token
 */
export const checkStatusAuth = oc
    .route({
    method: "GET",
    path: "/auth/check-status",
    summary: "Check user's onboarding status",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(z.object({}))
    .output(CheckStatusResponseSchema);
// ============================================
// Password Reset Contracts (Public)
// ============================================
/**
 * POST /orpc/auth/request-password-reset
 * Request password reset OTP
 */
export const requestPasswordResetAuth = oc
    .route({
    method: "POST",
    path: "/auth/request-password-reset",
    summary: "Request password reset OTP",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(RequestPasswordResetInputSchema)
    .output(RequestPasswordResetResponseSchema);
/**
 * POST /orpc/auth/verify-reset-otp
 * Verify password reset OTP
 */
export const verifyResetOtpAuth = oc
    .route({
    method: "POST",
    path: "/auth/verify-reset-otp",
    summary: "Verify password reset OTP",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(VerifyResetOtpInputSchema)
    .output(VerifyResetOtpResponseSchema);
/**
 * POST /orpc/auth/reset-password
 * Reset password with completion token
 */
export const resetPasswordAuth = oc
    .route({
    method: "POST",
    path: "/auth/reset-password",
    summary: "Reset password with completion token",
    tags: ["Auth"],
    successStatus: 200,
})
    .input(ResetPasswordInputSchema)
    .output(AuthMessageResponseSchema);
// ============================================
// Contract Routers
// ============================================
export const authContract = {
    register: registerAuth,
    login: loginAuth,
    logout: logoutAuth,
    refresh: refreshAuth,
    checkStatus: checkStatusAuth,
};
export const passwordResetContract = {
    requestPasswordReset: requestPasswordResetAuth,
    verifyResetOtp: verifyResetOtpAuth,
    resetPassword: resetPasswordAuth,
};
//# sourceMappingURL=auth.js.map